import posthog from "posthog-js";
import { supabase } from "@/lib/supabase";

export type PostHogIdentifyInput = {
  email: string;
  name?: string | null;
  location?: string | null;
  /** Account creation time — ISO person prop `account_created_at`. */
  createdAt?: Date | string | null;
  isEmailVerified?: boolean | null;
  /** All-time seller listing rows; omit to leave unset (or pass after fetch). */
  listingCount?: number | null;
};

/**
 * Person properties for `posthog.identify`.
 * PII (`email`, `name`) stay identify-only (PostHog D4).
 */
export function buildPostHogIdentifyProperties(
  input: PostHogIdentifyInput,
): Record<string, string | number | boolean> {
  const props: Record<string, string | number | boolean> = {
    email: input.email,
    email_verified: Boolean(input.isEmailVerified),
  };

  if (input.name) {
    props.name = input.name;
  }
  if (input.location) {
    props.location = input.location;
  }

  if (input.createdAt != null) {
    const iso =
      typeof input.createdAt === "string"
        ? new Date(input.createdAt).toISOString()
        : input.createdAt.toISOString();
    if (!Number.isNaN(Date.parse(iso))) {
      props.account_created_at = iso;
    }
  }

  if (
    typeof input.listingCount === "number" &&
    Number.isFinite(input.listingCount)
  ) {
    props.listing_count = Math.max(0, Math.floor(input.listingCount));
  }

  return props;
}

/** All-time count of items owned by the seller (any status still in `items`). */
export async function fetchSellerListingCount(
  sellerId: string,
): Promise<number | null> {
  try {
    const { count, error } = await supabase
      .from("items")
      .select("id", { count: "exact", head: true })
      .eq("seller_id", sellerId);

    if (error) {
      console.warn("[posthog] listing_count fetch failed:", error.message);
      return null;
    }
    return count ?? 0;
  } catch (error) {
    console.warn("[posthog] listing_count fetch failed:", error);
    return null;
  }
}

export type IdentifyPostHogUserOptions = PostHogIdentifyInput & {
  /** When true and `listingCount` omitted, query `items` for this distinct id. */
  fetchListingCount?: boolean;
};

/**
 * Identify the user with shared person props. Safe to fire-and-forget.
 */
export async function identifyPostHogUser(
  distinctId: string,
  options: IdentifyPostHogUserOptions,
): Promise<void> {
  try {
    let listingCount = options.listingCount;
    if (
      options.fetchListingCount &&
      (listingCount === undefined || listingCount === null)
    ) {
      listingCount = await fetchSellerListingCount(distinctId);
    }

    posthog.identify(
      distinctId,
      buildPostHogIdentifyProperties({
        ...options,
        listingCount,
      }),
    );
  } catch (error) {
    console.warn("[posthog] identify failed:", error);
  }
}
